/**
 * GiulioContext — memoria conversazionale cross-pagina.
 * Le pagine registrano il "contesto corrente" (listing, batch, filtri) via `useGiulioContext()`.
 * GiulioMiniChat lo invia al backend insieme al messaggio.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const GiulioCtx = createContext(null);
const SS_KEY = 'giulio.pageContext';

/** Mappa path → nome leggibile per il chip di contesto generico */
const PATH_LABELS = {
  '/':             'Home',
  '/esplora':      'Esplora terreni',
  '/mappa':        'Mappa',
  '/partite':      'Marketplace Partite',
  '/pubblica':     'Pubblica terreno',
  '/partite/pubblica': 'Pubblica partita',
  '/stima-quintali': 'Stima Quintali AI',
  '/suggeriti':    'Suggeriti per te',
  '/preferenze':   'Preferenze',
  '/dashboard':    'Dashboard',
  '/messaggi':     'Messaggi',
  '/chi-siamo':    'Chi siamo',
  '/contatti':     'Contatti',
  '/assistente':   'Assistente AI',
};

export function pathToLabel(path = '/') {
  if (PATH_LABELS[path]) return PATH_LABELS[path];
  const keys = Object.keys(PATH_LABELS).sort((a, b) => b.length - a.length);
  const key = keys.find(k => path.startsWith(k) && k !== '/');
  if (key) return PATH_LABELS[key];
  if (path.startsWith('/terreno/')) return 'Dettaglio terreno';
  if (path.startsWith('/partite/')) return 'Dettaglio partita';
  return 'AgriNexus';
}

export function GiulioProvider({ children }) {
  const [context, setContextState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const setContext = useCallback((ctx) => {
    setContextState(ctx);
    try {
      if (ctx) sessionStorage.setItem(SS_KEY, JSON.stringify(ctx));
      else sessionStorage.removeItem(SS_KEY);
    } catch {}
  }, []);
  const clearContext = useCallback(() => {
    setContextState(null);
    try { sessionStorage.removeItem(SS_KEY); } catch {}
  }, []);
  const value = useMemo(() => ({ context, setContext, clearContext }), [context, setContext, clearContext]);
  return <GiulioCtx.Provider value={value}>{children}</GiulioCtx.Provider>;
}

export function useGiulioContext() {
  const ctx = useContext(GiulioCtx);
  if (!ctx) throw new Error('useGiulioContext must be used within GiulioProvider');
  return ctx;
}

/**
 * Hook helper per registrare il contesto da una pagina.
 * NON pulisce all'unmount: il contesto persiste finché l'utente naviga via a un'altra pagina specifica
 * o lo rimuove manualmente dalla chip. Così quando apre /assistente in fullscreen, il contesto rimane.
 */
export function useRegisterGiulioContext(ctx) {
  const { setContext } = useGiulioContext();
  const key = ctx ? JSON.stringify({ k: ctx.kind, id: ctx.data?.listing_id || ctx.data?.batch_id, l: ctx.label }) : null;
  useEffect(() => {
    if (ctx && ctx.data) {
      // Salva solo i campi essenziali per non appesantire sessionStorage
      const lite = {
        kind: ctx.kind,
        label: ctx.label,
        data: ctx.kind === 'listing'
          ? { listing_id: ctx.data.listing_id, title: ctx.data.title }
          : { batch_id: ctx.data.batch_id, title: ctx.data.title },
      };
      setContext(lite);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
